'use client'

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { getCharacterById, type Character } from '@/lib/characters'

type AppMode = 'parent' | 'child'

interface AppModeContextValue {
  mode: AppMode
  selectedChildId: string | null
  selectedChildName: string | null
  selectedCharacter: Character | null
  isHydrated: boolean
  enterChildMode: (childId: string, childName: string) => void
  exitChildMode: () => void
  setSelectedCharacter: (character: Character | null) => void
}

const AppModeContext = createContext<AppModeContextValue | undefined>(undefined)

const STORAGE_KEY = 'santa-app-mode'

interface StoredMode {
  mode: AppMode
  selectedChildId: string | null
  selectedChildName: string | null
  selectedCharacterId: string | null
}

export function AppModeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setMode] = useState<AppMode>('parent')
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null)
  const [selectedChildName, setSelectedChildName] = useState<string | null>(null)
  const [selectedCharacter, setSelectedCharacter] = useState<Character | null>(null)
  const [isHydrated, setIsHydrated] = useState(false)
  const hasUserStateRef = useRef(false)

  // Load from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && !hasUserStateRef.current) {
      try {
        const parsed: StoredMode = JSON.parse(stored)
        setMode(parsed.mode)
        setSelectedChildId(parsed.selectedChildId)
        setSelectedChildName(parsed.selectedChildName)
        if (parsed.selectedCharacterId) {
          const character = getCharacterById(parsed.selectedCharacterId)
          if (character) {
            setSelectedCharacter(character)
          }
        }
      } catch (error) {
        console.error('Error parsing stored mode:', error)
      }
    }
    setIsHydrated(true)
  }, [])

  // Save to localStorage whenever state changes
  useEffect(() => {
    if (isHydrated) {
      const toStore: StoredMode = {
        mode,
        selectedChildId,
        selectedChildName,
        selectedCharacterId: selectedCharacter?.id || null
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore))
    }
  }, [mode, selectedChildId, selectedChildName, selectedCharacter, isHydrated])

  const enterChildMode = useCallback((childId: string, childName: string) => {
    hasUserStateRef.current = true
    setMode('child')
    setSelectedChildId(childId)
    setSelectedChildName(childName)
  }, [])

  const exitChildMode = useCallback(() => {
    hasUserStateRef.current = true
    setMode('parent')
    setSelectedChildId(null)
    setSelectedChildName(null)
    setSelectedCharacter(null)
  }, [])

  const handleSetSelectedCharacter = useCallback((character: Character | null) => {
    hasUserStateRef.current = true
    setSelectedCharacter(character)
  }, [])

  const value: AppModeContextValue = useMemo(() => ({
    mode,
    selectedChildId,
    selectedChildName,
    selectedCharacter,
    isHydrated,
    enterChildMode,
    exitChildMode,
    setSelectedCharacter: handleSetSelectedCharacter
  }), [mode, selectedChildId, selectedChildName, selectedCharacter, isHydrated, enterChildMode, exitChildMode, handleSetSelectedCharacter])

  return (
    <AppModeContext.Provider value={value}>
      {children}
    </AppModeContext.Provider>
  )
}

export function useAppMode() {
  const context = useContext(AppModeContext)
  if (context === undefined) {
    throw new Error('useAppMode must be used within an AppModeProvider')
  }
  return context
}
